/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable no-constant-condition */
/* eslint-disable react/no-unescaped-entities */
import React, { useEffect, useState } from 'react';
import '../beforeLoading/BeforeLoading.less';
import { ScanOutlined, LeftOutlined, DeleteTwoTone, EnterOutlined } from '@ant-design/icons';
import { Button, Form, Input } from 'antd';
import { useAuth } from '@isocel/front-common';
import { useHistory, useLocation } from 'react-router-dom';
import { isEmpty } from 'lodash';

import { usePackagingFlow } from '../../context/PackagingFlowContext';
import './QrReading.less';
import { getVoucherNumberDetails, getPackagingFlowByPackagingVoucher } from '../../queries/api/packaging-flow';
import { PackagingFlow, VoucherResponse } from '../../queries/api/types';
import { useErrorMessages } from '../../context/ErrorMessagesContext';
import { usePackagingConfig } from '../../queries/package';

const QrReading: React.FC = React.memo(() => {
    const { data: packageConfig } = usePackagingConfig();
    const { setPackagingFlowData, setOriginalQuantities, vouchers, setVouchers } = usePackagingFlow();
    const history = useHistory();
    const [scanningMessage, setScanningMessage] = useState(false);
    const [scannedResult, setScannedResult] = useState('');
    const { user } = useAuth();
    const location = useLocation<{ from?: string; scannedData?: string[] }>();
    const { errorMessages, setErrorMessages } = useErrorMessages(); // State to store error messages
    useEffect(() => {
        if (location.state?.from === 'qrParcs') {
            setErrorMessages([]);
        }
        vouchers.length > 0 ? setScanningMessage(true) : setScanningMessage(false);
        if (location.state?.scannedData && Array.isArray(location.state.scannedData)) {
            location.state.scannedData.forEach((data) => {
                scanCode(data, false);
            });
        }

        const handleBeforeUnload = () => {
            history.replace({ ...location, state: {} });
        };

        window.addEventListener('beforeunload', handleBeforeUnload);

        return () => {
            window.removeEventListener('beforeunload', handleBeforeUnload);
            if (history.action === 'POP') {
                history.replace({ ...location, state: {} });
            }
        };
    }, [location.state?.scannedData]); // Add location.state?.scannedData as a dependency

    const scanCode = async (voucherNumber: string, isVoucher: boolean) => {
        if (!voucherNumber) {
            return;
        }
        try {
            if (
                !vouchers.some((voucher) => voucher.voucherNumber === voucherNumber) &&
                !vouchers.some((voucher) => voucher.flowReceptionId === voucherNumber)
            ) {
                const response: VoucherResponse = await getVoucherNumberDetails(
                    voucherNumber,
                    user?.organization?.id || '',
                    isVoucher
                );
                if (response.success) {
                    setVouchers((prev) => [...prev, response.voucher]);
                    setScanningMessage(true);
                    setScannedResult('');
                } else {
                    let errorMessage = 'Une erreur inconnue est survenue';

                    if (response.message?.startsWith('Voucher is older than')) {
                        errorMessage = `Le bon n° ${response.voucher?.voucherNumber || voucherNumber} est expiré`;
                    } else if (response.message === 'Voucher is still in Created status') {
                        errorMessage = `Le bon n° ${
                            response.voucher?.voucherNumber || voucherNumber
                        } a déjà été utilisé.`;
                    } else if (response.message === 'organization not matched') {
                        errorMessage = `Le bon n° ${
                            response.voucher?.voucherNumber || voucherNumber
                        } est inconnu pour votre SCA`;
                    }
                    // Add the error message to the array
                    setErrorMessages((prev) => [...prev, errorMessage]);
                    setScannedResult('');
                }
            } else {
                setScanningMessage(true);
                setScannedResult('');
            }
        } catch (error) {
            setScannedResult('');
            const unknownError = `Un bon inconnu a été saisi`;
            setErrorMessages((prev) => [...prev, unknownError]); // Add the error message to the array
        }
    };

    const openScannerPage = () => {
        history.push('/qrCamera');
    };
    const handleSave = async () => {
        const response = await getPackagingFlowByPackagingVoucher(vouchers.map((voucher) => voucher._id));

        // 1. SCA logic and package filtering/updating
        const filteredResponse = response.map((flow: PackagingFlow) => {
            // Check if the voucher starts with "SCA-"
            const isSCAVoucher = vouchers.some(
                (v) => v._id === flow.packagingVouchers?.[0]?._id && v.voucherNumber.startsWith('SCA-')
            );

            // Filter packages based on the voucher type
            const filteredPackages = flow.packages.filter((pkg) => {
                if (isSCAVoucher) {
                    // For "SCA-" vouchers, only include packages where toBeReturned > 0
                    return (pkg?.toBeReturned ?? 0) > 0;
                }
                // For other vouchers, include all packages
                return true;
            });

            // Update the filtered packages
            filteredPackages.forEach((pkg) => {
                // Update package attributes for "SCA-" vouchers
                if (isSCAVoucher) {
                    pkg.goodCount = pkg.toBeReturned || 0;
                    pkg.badCount = 0;
                    pkg.toBeReturned = 0;
                }
            });

            // Return the updated flow with filtered packages
            return {
                ...flow,
                packages: filteredPackages,
            };
        });

        // 2. Combine all flows into a single packagingFlow (aggregation logic)
        if (!filteredResponse.length) return;

        // Start with the first flow as base
        const baseFlow = { ...filteredResponse[0] };
        // Merge all packagingVouchers
        baseFlow.packagingVouchers = filteredResponse.flatMap((flow: PackagingFlow) => flow.packagingVouchers || []);
        // Merge all packages
        const allPackages = filteredResponse.flatMap((flow: PackagingFlow) => flow.packages || []);
        const filteredPackagesByConfig = allPackages
            .filter((pkg: any) => {
                const packageId = typeof pkg.package === 'string' ? pkg.package : pkg.package.id;
                const configItem = packageConfig?.packages.find((p) => p.id === packageId);
                return configItem && configItem.rental === false;
            })
            .map((pkg: any) => ({
                ...pkg,
                badCount: 0,
            }));
        // Aggregate packages by packageId
        const packageMap = new Map();
        filteredPackagesByConfig.forEach((pkg: any) => {
            const packageId = typeof pkg.package === 'string' ? pkg.package : pkg.package.id;
            if (!packageMap.has(packageId)) {
                packageMap.set(packageId, { ...pkg });
            } else {
                const existing = packageMap.get(packageId);
                existing.goodCount = Number(existing.goodCount) + Number(pkg.goodCount);
                existing.badCount = 0;
                existing.shippedQuantity = Number(existing.shippedQuantity) + Number(pkg.shippedQuantity);
            }
        });

        baseFlow.packages = Array.from(packageMap.values());
        // Set originalQuantities for each packageId
        const totalQuantities: { [key: string]: number } = {};
        baseFlow.packages.forEach((pkg: any) => {
            const packageId = typeof pkg.package === 'string' ? pkg.package : pkg.package.id;
            const goodCount = Number(pkg.goodCount) || 0;
            const badCount = Number(pkg.badCount) || 0;
            totalQuantities[packageId] = (totalQuantities[packageId] || 0) + (goodCount + badCount);
        });
        Object.entries(totalQuantities).forEach(([packageId, count]) => {
            setOriginalQuantities(packageId, count);
        });

        setPackagingFlowData([baseFlow]);
        history.push('/qrPackageTrack');
    };

    const handleDeleteVoucher = (id: string) => {
        const updatedVouchers = vouchers.filter((voucher) => voucher.voucherNumber.trim() !== id.trim());
        setVouchers(updatedVouchers);
        if (isEmpty(updatedVouchers)) {
            setScanningMessage(false);
        }
    };
    return (
        <>
            <div className="title mb-16">
                <LeftOutlined className="back-icon" onClick={() => history.push('/qrParcs')} />
            </div>
            <div className="qr-reading-page">
                <div className="form-container">
                    <div className="title">
                        <h2>Veuillez saisir le(s) n° de bon(s) d’emballages</h2>
                    </div>
                    <Form layout="vertical">
                        <Form.Item label="N° de bon d'emballage">
                            <div className="input-with-icon">
                                <Input
                                    id="bon-emballage"
                                    placeholder="Saisir le n° de bon d'emballage"
                                    className="input-field"
                                    value={scannedResult}
                                    onChange={(e) => setScannedResult(e.target.value)}
                                    onKeyDown={async (e) => {
                                        if (e.key === 'Enter') {
                                            e.preventDefault();
                                            await scanCode(scannedResult, true);
                                        }
                                    }}
                                />
                                <EnterOutlined
                                    onClick={async () => await scanCode(scannedResult, true)}
                                    className="scan-icon"
                                />
                                <ScanOutlined onClick={openScannerPage} className="ml-12 scan-icon" />
                            </div>
                        </Form.Item>
                        {scanningMessage && (
                            <div className="scanning-message">
                                <p> Bons d'emballages saisis et valides</p>
                                <ul>
                                    {vouchers.map((voucher, index) => (
                                        <li key={index} className="voucher-list">
                                            {' '}
                                            n° {voucher.voucherNumber}{' '}
                                            <DeleteTwoTone
                                                className="delete-icon"
                                                onClick={() => handleDeleteVoucher(voucher.voucherNumber)}
                                            />
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        )}
                        <Form.Item>
                            <div className="back-button">
                                <Button
                                    type="primary"
                                    htmlType="submit"
                                    onClick={handleSave}
                                    disabled={!scanningMessage}
                                >
                                    Suivant
                                </Button>
                            </div>
                        </Form.Item>
                    </Form>
                    <div className="error-messages">
                        {errorMessages.length > 0 && (
                            <div className="error-messages">
                                <span className="text-red">Erreurs</span>
                                <ul className="text-red">
                                    {errorMessages.map((message, index) => (
                                        <li key={index} className="error-message">
                                            {message}
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </>
    );
});

export default QrReading;
